<script setup>
import { ref, computed, watch } from 'vue'
import { Head, router, useForm } from '@inertiajs/vue3'
import { PhUserPlus, PhCamera } from "@phosphor-icons/vue"
import Layout   from '@pages/Shared/layouts/AdminLayout.vue'
import Table    from '@pages/Shared/ui/Table.vue'
import Spinner  from '@pages/Shared/ui/Spinner.vue'
import Modal    from '@pages/Shared/Modal.vue'
import Prompt   from '@pages/Shared/Prompt.vue'
import Input    from '@pages/Shared/form/Input.vue'
import Select   from '@pages/Shared/form/Select.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import { flashToast } from "@/toast.js"
import userMale   from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'

/* -------------------- PROPS -------------------- */
const props = defineProps({
    users:   Object,
    roles:   Array,
    filters: Object,
})

/* -------------------- STATE -------------------- */
const openModal   = ref(false)
const modalStatus = ref(null)
const modalTitle  = ref('')
const showPrompt  = ref(false)
const fileInput   = ref(null)

const filters = ref({ search: null, gender: null, role: null, status: null, ...props.filters })

/* -------------------- FORM -------------------- */
const form = useForm({
    _method:  null,
    id:       null,
    avatar:   null,
    preview:  null,
    name:     '',
    email:    '',
    gender:   null,
    dob:      '',
    phone:    '',
    address:  '',
    role_id:  '',
    status:   null,
})

/* -------------------- TABLE COLUMNS -------------------- */
const columns = [
    { key: 'index',     label: '#',      type: 'index'   },
    { key: 'avatar',    label: 'Avatar', type: 'slot'    },
    { key: 'name',      label: 'Name'                    },
    { key: 'email',     label: 'Email'                   },
    { key: 'gender',    label: 'Gender'                  },
    { key: 'dob',       label: 'DOB'                     },
    { key: 'phone',     label: 'Phone'                   },
    { key: 'address',   label: 'Address'                 },
    { key: 'role.name', label: 'Role'                    },
    { key: 'status',    label: 'Status', type: 'slot'    },
    { key: 'actions',   label: '',       type: 'actions' },
]

/* -------------------- OPTIONS -------------------- */
const roleOptions = computed(() =>
    props.roles.map(r => ({
        label: r.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        value: r.id,
    }))
)

/* -------------------- FILTERS -------------------- */
watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    )
    router.get('/admin/users', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value) => { filters.value.search = value; applyFilters() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters() }

/* -------------------- MODAL HELPERS -------------------- */
const setFormData = (selected) => {
    form.id      = selected.id
    form.avatar  = selected.avatar
    form.name    = selected.name
    form.email   = selected.email
    form.gender  = selected.gender
    form.dob     = selected.dob
    form.phone   = selected.phone
    form.address = selected.address
    form.role_id = selected.role?.id ?? ''
    form.status  = selected.status
    form.preview = null
}

const handleModalClose = () => {
    if (form.preview) URL.revokeObjectURL(form.preview)
    modalStatus.value = null
    modalTitle.value  = null
    openModal.value   = false
    form.clearErrors()
    form.reset()
}

const openAddUserModal = () => {
    form.reset()
    modalTitle.value  = 'Add User'
    modalStatus.value = 'create'
    openModal.value   = true
}

const editUser = (id) => {
    const selected = props.users.data.find(u => u.id === id)
    if (!selected) return
    setFormData(selected)
    modalTitle.value  = 'Edit User'
    modalStatus.value = 'edit'
    openModal.value   = true
}

const viewUser = (id) => {
    const selected = props.users.data.find(u => u.id === id)
    if (!selected) return
    setFormData(selected)
    modalTitle.value  = 'View User'
    modalStatus.value = 'view'
    openModal.value   = true
}

const deleteUser  = (id) => { form.id = id; showPrompt.value = true }

const confirmDelete = () => {
    form.delete(`/admin/users/${form.id}`, {
        onSuccess: (page) => {
            showPrompt.value = false
            form.reset()
            flashToast(page.props.flash)
        },
    })
}

const handleFileSelected = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (form.preview) URL.revokeObjectURL(form.preview)
    form.avatar  = file
    form.preview = URL.createObjectURL(file)
}

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => {
            if (form.preview) URL.revokeObjectURL(form.preview)
            form.clearErrors()
            form.reset()
            openModal.value = false
            flashToast(page.props.flash)
        },
        onError: () => flashToast({ type: 'error', message: 'Check form for errors' }),
    }

    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/admin/users/${form.id}`, options)
    } else {
        form.post('/admin/users', options)
    }
}
</script>

<template>
    <Layout>
        <Head title="Users" />

        <!-- Delete Prompt -->
        <Prompt
            title="user"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <!-- Modal -->
        <Modal
            v-model="openModal"
            :title="modalTitle"
            @close="handleModalClose"
        >
            <form class="p-6 space-y-4" @submit.prevent="handleSubmit">

                <!-- Avatar -->
                <div class="relative w-28 h-28 mx-auto mb-2">
                    <img
                        :src="form.preview
                            ? form.preview
                            : (form.avatar
                                ? `/storage/${form.avatar}`
                                : (form.gender === 'Male' ? userMale : userFemale))"
                        class="w-full h-full object-cover rounded-full ring-4 ring-gray-100"
                        alt="Avatar"
                    />
                    <span
                        v-if="modalStatus !== 'view'"
                        class="absolute bottom-0 right-0 p-1.5 rounded-full text-white cursor-pointer transition"
                        :class="modalStatus === 'edit' ? 'bg-edit hover:bg-edit-light' : 'bg-primary hover:bg-primary-light'"
                        @click="fileInput?.click()"
                    >
                        <PhCamera :size="16" />
                    </span>
                    <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handleFileSelected" />
                </div>

                <Input v-model="form.name" label="Full Name" placeholder="Enter full name"
                    :disabled="modalStatus === 'view'" :error="form.errors.name" />

                <Input v-model="form.email" label="Email" type="email" placeholder="Email address"
                    :disabled="modalStatus === 'view'" :error="form.errors.email" />

                <Select label="Gender" v-model="form.gender" :options="['Male', 'Female']"
                    placeholder="Select Gender" :disabled="modalStatus === 'view'" :error="form.errors.gender" />

                <Input v-model="form.dob" type="date" label="Date of Birth"
                    :disabled="modalStatus === 'view'" :error="form.errors.dob" />

                <Input v-model="form.phone" label="Phone Number" placeholder="Phone number"
                    :disabled="modalStatus === 'view'" :error="form.errors.phone" />

                <Textarea label="Address" v-model="form.address" placeholder="Enter address..."
                    :rows="2" :disabled="modalStatus === 'view'" :error="form.errors.address" />

                <Select label="User Role" v-model="form.role_id" :options="roleOptions"
                    placeholder="Select role" :disabled="modalStatus === 'view'" :error="form.errors.role_id" />

                <Select
                    v-if="modalStatus === 'edit' || modalStatus === 'view'"
                    label="Status" v-model="form.status"
                    :options="['Active', 'Inactive'].map(s => ({ label: s, value: s }))"
                    placeholder="Select status"
                    :disabled="modalStatus === 'view'" :error="form.errors.status"
                />

                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold transition
                           flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="modalStatus === 'edit' ? 'bg-edit hover:bg-edit-light' : 'bg-primary hover:bg-primary-light'"
                    :disabled="form.processing"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    {{ modalStatus === 'edit' ? 'Update User' : 'Add User' }}
                </button>

            </form>
        </Modal>

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Users</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all system users</p>
            </div>
            <button
                @click="openAddUserModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhUserPlus :size="18" /> Add User
            </button>
        </div>

        <!-- Table -->
        <Table
            :data="users"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'gender', label: 'Gender',
                    options: [
                        { label: 'All Genders', value: null },
                        { label: 'Male',        value: 'Male'   },
                        { label: 'Female',      value: 'Female' },
                    ],
                    value: filters.gender,
                },
                {
                    key: 'role', label: 'Role',
                    options: [
                        { label: 'All Roles', value: null },
                        ...roles.map(r => ({
                            label: r.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                            value: r.id,
                        })),
                    ],
                    value: filters.role,
                },
                {
                    key: 'status', label: 'Status',
                    options: [
                        { label: 'All Statuses', value: null },
                        { label: 'Active',       value: 'active'   },
                        { label: 'Inactive',     value: 'inactive' },
                    ],
                    value: filters.status,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="viewUser"
            @edit="editUser"
            @delete="deleteUser"
        >
            <template #avatar="{ row }">
                <img
                    class="w-9 h-9 rounded-full object-cover ring-2 ring-gray-100"
                    :src="row.avatar ? `/storage/${row.avatar}` : (row.gender === 'Male' ? userMale : userFemale)"
                    :alt="row.name"
                />
            </template>

            <template #status="{ row }">
                <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    :class="row.status?.toLowerCase() === 'active'
                        ? 'bg-green-50 text-green-700 border-green-100'
                        : 'bg-orange-50 text-orange-600 border-orange-100'"
                >
                    <span class="w-1.5 h-1.5 rounded-full"
                        :class="row.status?.toLowerCase() === 'active' ? 'bg-green-500' : 'bg-orange-400'">
                    </span>
                    {{ row.status }}
                </span>
            </template>
        </Table>
    </Layout>
</template>